
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import Booking, { TableAssignmentStatus } from "@/domain/booking/Booking";
import DialogBanner from "@/components/app/DialogBanner.vue";
import BookingDetailsForm from "@/components/booking/components/BookingDetailsForm.vue";
import BookingViewDatePicker from "@/components/booking/components/BookingViewDatePicker.vue";
import BookingPropertiesPicker from "@/components/booking/components/BookingPropertiesPicker.vue";
import TimeService from "@/services/TimeService";
import Room from "@/domain/restaurant/Room";
import UtilService from "@/services/UtilService";
import RemoteServices from "@/services/RemoteServices";
import Table from "@/domain/restaurant/Table";
import ConfirmationDialog from "@/components/alert/ConfirmationDialog.vue";
@Component({
  components: {
    ConfirmationDialog,
    BookingPropertiesPicker,
    BookingViewDatePicker,
    BookingDetailsForm,
    DialogBanner
  }
})
export default class BookingDialog extends Vue {
  @Prop({ default: false })
  value!: boolean;

  @Prop({ default: null })
  booking!: Booking;

  @Prop({ default: () => {} })
  tables!: { [key: number]: Table };

  bookingCopy: Booking = Booking.copy(this.booking);

  currentDate: Date = new Date(this.booking.bookingDateTime);

  currentTab = 0;

  tableAssignmentStatus: TableAssignmentStatus | null = Booking.getTableAssignmentStatusFromString(
    this.bookingCopy.tableAssignmentStatus
  );

  isBookingPropertiesValid = false;

  isBookingDetailsFormValid = false;

  selectedTime = "";

  selectedRoom: Room | null = null;

  rooms: { [key: number]: Room } = {};

  showTimePicker = false;

  loading = false;

  resetTableAssignmentStatus() {
    this.bookingCopy.tableAssignmentStatus = undefined;
    this.tableAssignmentStatus = null;
    this.setRequestedTableId(null);
  }

  setIsRequestedTable() {
    this.bookingCopy.setRequestedTable();
    this.tableAssignmentStatus = Booking.getTableAssignmentStatusFromString(
      this.bookingCopy.tableAssignmentStatus
    );
  }

  setIsVIP() {
    this.bookingCopy.setVIP();
    this.tableAssignmentStatus = Booking.getTableAssignmentStatusFromString(
      this.bookingCopy.tableAssignmentStatus
    );
  }

  setShowTimePicker(newVal: boolean) {
    this.showTimePicker = newVal;
  }

  get confirmationDialog() {
    return this.$refs.confirmationDialog as ConfirmationDialog;
  }

  get selectedTable(): Table | null {
    if (this.bookingCopy.tableId != null)
      return this.tables[this.bookingCopy.tableId];
    return null;
  }

  setRequestedTableId(newTableId: number | null) {
    if (newTableId == null) this.bookingCopy.tableId = undefined;
    else this.bookingCopy.tableId = newTableId;
  }

  get isPropertiesTab() {
    return this.currentTab === 1;
  }

  get isCustomerDetailsTab() {
    return this.currentTab === 0;
  }

  resetCurrentDate() {
    return new Date(this.booking.bookingDateTime);
  }

  setBookingDate(newDate: any) {
    this.currentDate = newDate;
  }

  setBookingTime(newTime: string) {
    this.selectedTime = newTime;
    this.setBookingDateTime();
  }

  @Watch("currentTab")
  resetCopy() {
    this.bookingCopy = Booking.copy(this.booking);
    this.currentDate = new Date(this.booking.bookingDateTime);
    this.selectedTime = TimeService.getTimeFromISOString(
      this.booking.bookingDateTime
    );
    this.tableAssignmentStatus = Booking.getTableAssignmentStatusFromString(
      this.booking.tableAssignmentStatus
    );
    this.getPreferredRoomFromBooking();
    this.resetCurrentDate();
  }

  @Watch("booking")
  reset() {
    this.getRoomDictionary();
    this.currentTab = 0;
    this.loading = false;
    if (this.booking != null && !!this.booking.bookingDateTime) {
      this.resetCopy();
    }
  }

  setBookingDateTime() {
    if (this.selectedTime !== "") {
      TimeService.setDateHours(this.currentDate, this.selectedTime);
      this.bookingCopy.bookingDateTime = TimeService.getISO8601DateTimeString(
        this.currentDate
      );
    } else this.bookingCopy.bookingDateTime = "";
  }

  get dateTimeSet() {
    return this.bookingCopy.bookingDateTime !== "";
  }

  setSelectedRoom(newRoom: Room) {
    this.selectedRoom = newRoom;
  }

  getPreferredRoomFromBooking() {
    if (this.booking.preferredRoomId != null)
      this.selectedRoom = this.rooms[this.booking.preferredRoomId];
    else this.selectedRoom = null;
  }

  async getRoomDictionary() {
    this.rooms = UtilService.getRoomDict(
      await this.$store.getters.getSelectedRestaurant
    );
  }

  get bookingChanged() {
    return Booking.haveDifferences(this.bookingCopy, this.booking);
  }

  get dateTimeChanged() {
    if (this.selectedTime === "" || this.currentDate == null) return false;
    this.setBookingDateTime();
    return this.bookingChanged;
  }

  get dialog() {
    return this.value;
  }

  set dialog(newValue) {
    this.$emit("input", newValue);
    this.reset();
  }

  updateDate(newDate: Date) {
    this.currentDate = newDate;
  }

  async updateBookingProperties() {
    this.loading = true;
    if (this.isBookingPropertiesValid)
      try {
        this.bookingCopy = await RemoteServices.updateBookingProperties(
          this.bookingCopy
        );
        await this.$store.dispatch("success", this.$t("booking.edit.success"));
        this.emitRefreshBookings();
        this.dialog = false;
      } catch (error) {
        await this.$store.dispatch("error", error.message);
      }
    else
      console.log("Update Customer Details Failed,Customer Details Invalid.");
    this.loading = false;
  }

  async updateCustomerDetails() {
    this.loading = true;
    if (this.isBookingDetailsFormValid)
      try {
        this.bookingCopy = await RemoteServices.updateBookingCustomerDetails(
          this.bookingCopy
        );
        await this.$store.dispatch("success", this.$t("booking.edit.success"));
        this.updateBooking();
        this.dialog = false;
      } catch (error) {
        await this.$store.dispatch("error", error.message);
      }
    else
      console.log("Update Customer Details Failed,Customer Details Invalid.");
    this.loading = false;
  }

  emitRefreshBookings() {
    this.$emit("new-booking", this.bookingCopy);
  }

  createNewBookingFromExisting() {
    this.$emit(
      "new-booking-from-existing",
      Booking.getBookingCopyToCreateNewBooking(this.bookingCopy)
    );
    this.dialog = false;
  }

  updateBooking() {
    Booking.updateBooking(this.booking, this.bookingCopy);
  }

  async gdprDeleteCustomerData() {
    if (
      await this.confirmationDialog.open(
        this.$tc("booking.gdpr.message"),
        this.$t("booking.gdpr.confirmation") +
          "\n" +
          this.booking.customerName +
          "\n" +
          this.$t("delete.warning")
      )
    ) {
      try {
        this.loading = true;
        this.bookingCopy = await RemoteServices.deleteBookingCustomerDataGDPR(
          this.booking
        );
        await this.$store.dispatch("success", this.$t("booking.gdpr.success"));
        this.updateBooking();
        this.dialog = false;
      } catch (error) {
        await this.$store.dispatch("error", error.message);
      }
      this.loading = false;
    }
  }
}
